import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import type { GraphRendererPluginInterface } from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import type { DisplayObject, interaction } from 'pixi.js';

interface ClickedElement<T extends DisplayObject> {
  element: T;
  prevClickCount: number;
}

interface MultiClickEventHandlers<T> {
  onClick?: (element: T) => void;
  onDoubleClick?: (element: T) => void;
  /** This is called also for single and double click, so you can choose which approach suits to you better */
  onMultiClick?: (clickCount: number, element: T) => void;
}

/**
 * Improved mouse click event processing for PIXI elements
 *
 * We need more from click event emission than PIXI can offer for following reasons:
 * 1) We need double clicks. PIXI has no double click event support at all,
 *    plus it uses browser 'pointer*' (pointerdown etc.) events internally, so we can't
 *    read click count from pixiEvent.data.originalEvent.detail (this is always 0
 *    for pointer* events).
 * 2) We need to be able to prevent click events in particular situations (e.g. mousedown
 *    followed by dragging interaction shouldn't initiate a click).
 *
 * This class offers solution to both above cases. It collects number of subsequent clicks
 * from window mouseup event, so we don't need to implement any proprietary double click timing.
 *
 * Notice: normally, click is considered as subsequent mousedown and mouseup *on the same element*.
 * Our implementation considers click after mouseup *anywhere*. This is because we need to use
 * window mouseup, so it would be difficult to detect where the mouse was released, and this is
 * not a concern for us at the moment.
 *
 * @param pluginInterface Must be provided to ensure dom event listener cleanup on graph app unmount.
 * @param eventHandlers Just single click event handler for single, double or n-count click.
 *   Dispatching to multiple event listeners is not a concern of this class.
 *
 * @example
 * declare const myDOs: MyDisplayObject[];
 * const clickDispatcher = new MultiClickDispatcher<MyDisplayObject>(
 *   pluginInterface,
 *   { onDoubleClick: e => { console.log('clicked on', e); },
 * );
 * myDOs.forEach(myDO => {
 *   myDO.on('mousedown', clickDispatcher.handleMouseDown);
 * });
 */
export class MultiClickDispatcher<T extends DisplayObject> {
  private clickedElement: ClickedElement<T> | null = null;

  constructor(
    pluginInterface: GraphRendererPluginInterface,
    private eventHandlers: MultiClickEventHandlers<T>,
  ) {
    const domEventListeners = new ManagedDomEventListeners([
      window,
      'mouseup',
      this.handleWindowMouseUp,
    ]);
    pluginInterface.events.addListener('beforeUnmount', () => {
      domEventListeners.remove();
    });
  }

  public handleMouseDown = (e: interaction.InteractionEvent): void => {
    if (
      this.clickedElement === null ||
      this.clickedElement.element !== e.currentTarget
    ) {
      // Update only when different element is clicked, do not update prevClickCount here.
      // Reason: PIXI uses pointerdown event behind the scenes, which has always detail === 0,
      // so we can't determine multiclicks here - that job is done in window mouseup handler.
      this.clickedElement = {
        // Can't do without assertion because InteractionEvent isn't generic so we can't restrict
        // element type on class interface.
        element: e.currentTarget as T,
        prevClickCount: 0,
      };
    }
  };

  /**
   * Useful e.g. for drag operations - calling this method lets this class know that
   * following mouseup won't result in click, e.g. because dragging operation started.
   */
  public interruptClick = (): void => {
    this.clickedElement = null;
  };

  private handleWindowMouseUp = (e: MouseEvent) => {
    if (this.clickedElement !== null) {
      if (this.clickedElement.prevClickCount !== e.detail - 1) {
        // In n-clicks-in-row (including single click) interactions, this.clickedElement.prevClickCount
        // should be always e.detail - 1 (see https://developer.mozilla.org/en-US/docs/Web/API/UIEvent/detail).
        // If the number differs (expected case is prevClickCount > 0 and e.detail === 1, but all others are invalid
        // too), it means that we just remembered some clicked element for possible n-clicks interaction,
        // but that ended and current mouseup belongs to another interaction.
        // This is not an error, just the simplest place to reset this.clickedElement so that we don't have to
        // set up custom timers to decide what is a double click and just rely on event.detail.
        this.clickedElement = null;
        return;
      }
      this.clickedElement.prevClickCount += 1;
      switch (e.detail) {
        case 1:
          this.eventHandlers.onClick?.(this.clickedElement.element);
          break;
        case 2:
          this.eventHandlers.onDoubleClick?.(this.clickedElement.element);
          break;
        default:
          break;
      }
      this.eventHandlers.onMultiClick?.(e.detail, this.clickedElement.element);
    }
  };
}
